/**
 * DashboardHeader Component
 *
 * Sticky header with title, period selector, role badges, and admin actions
 */

import React from 'react';
import { Box, Typography, Chip, Button, Tooltip, useTheme, useMediaQuery, Select, MenuItem, FormControl, IconButton } from '@mui/material';
import { TrendingUp, Add, Groups, Lock, LockOpen, CalendarMonth, EditOutlined, StarOutline } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';

export const DashboardHeader: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { isDarkMode, data, permissions, dialogs } = useOKRDashboardContext();
    const {
        activePeriod,
        viewedPeriod,
        periods,
        isLocked,
        userContext,
        handleViewPeriod,
        handleSetActivePeriod,
        handleLockToggle,
    } = data;

    const hasOpenPeriod = periods.some((p: any) => !p.is_locked || p.is_locked === 0);
    const isViewingNonActive = viewedPeriod?.id !== activePeriod?.id;

    const handlePeriodChange = (event: any) => {
        const newPeriodId = event.target.value;
        if (newPeriodId && newPeriodId !== viewedPeriod?.id) {
            handleViewPeriod(newPeriodId);
        }
    };

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: isDarkMode ? '#0f172a' : '#ffffff',
                borderBottom: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}`,
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2, sm: 2.5, md: 3 },
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: { xs: 2, md: 0 },
            }}>
                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
                        {/* Icon */}
                        <Box
                            sx={{
                                width: { xs: 40, sm: 56 },
                                height: { xs: 40, sm: 56 },
                                borderRadius: { xs: 2, sm: 3 },
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
                                flexShrink: 0,
                            }}
                        >
                            <TrendingUp sx={{ color: '#fff', fontSize: { xs: 22, sm: 30 } }} />
                        </Box>

                        {/* Title & Badges */}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: '1.15rem', sm: '1.4rem', md: '1.75rem' },
                                    color: theme.palette.text.primary,
                                    letterSpacing: '-0.03em',
                                    fontFamily: '"Inter", "SF Pro Display", sans-serif',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                OKR Command Center
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                {/* Period Selector (Admin) or Period Chip (Others) */}
                                {permissions.canCreateOrgOKR && periods.length > 0 ? (
                                    <>
                                        <FormControl variant="standard" size="small">
                                            <Select
                                                value={viewedPeriod?.id || ''}
                                                onChange={handlePeriodChange}
                                                disableUnderline
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    color: isViewingNonActive ? '#f59e0b' : '#818cf8',
                                                    bgcolor: isViewingNonActive ? 'rgba(245, 158, 11, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                                                    borderRadius: 3,
                                                    px: 1.5,
                                                    height: 28,
                                                    border: `1px solid ${isViewingNonActive ? 'rgba(245, 158, 11, 0.25)' : 'rgba(99, 102, 241, 0.25)'}`,
                                                    '& .MuiSelect-select': {
                                                        py: 0,
                                                        pr: '24px !important',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: isViewingNonActive ? '#f59e0b' : '#818cf8',
                                                        fontSize: 18,
                                                    },
                                                }}
                                            >
                                                {periods.map((period: any) => (
                                                    <MenuItem key={period.id} value={period.id} sx={{ fontSize: '0.85rem' }}>
                                                        {period.name}
                                                        {period.is_active === 1 && ' \u2605'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Tooltip title="Edit period" arrow>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => viewedPeriod && dialogs.openEditPeriodDialog(viewedPeriod)}
                                                    disabled={!viewedPeriod}
                                                    sx={{
                                                        color: '#818cf8',
                                                        width: 28,
                                                        height: 28,
                                                        '&:hover': {
                                                            bgcolor: 'rgba(99, 102, 241, 0.15)',
                                                        },
                                                    }}
                                                >
                                                    <EditOutlined sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {isViewingNonActive && (
                                            <>
                                                <Chip
                                                    label="VIEWING"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(245, 158, 11, 0.15)',
                                                        color: '#f59e0b',
                                                        fontWeight: 800,
                                                        fontSize: '0.7rem',
                                                        height: 26,
                                                        letterSpacing: '0.05em',
                                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                                    }}
                                                />
                                                <Tooltip title="Set this period as the active working period" arrow>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<StarOutline sx={{ fontSize: 14 }} />}
                                                        onClick={() => handleSetActivePeriod(viewedPeriod!.id)}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            height: 26,
                                                            borderRadius: 3,
                                                            px: 1.5,
                                                            textTransform: 'none',
                                                            borderColor: '#f59e0b',
                                                            color: '#f59e0b',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(245, 158, 11, 0.1)',
                                                                borderColor: '#f59e0b',
                                                            },
                                                        }}
                                                    >
                                                        Set as Active
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <Chip
                                        label={viewedPeriod?.name || activePeriod?.name || 'No Period'}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(99, 102, 241, 0.15)',
                                            color: '#818cf8',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            height: 28,
                                            letterSpacing: '-0.01em',
                                            border: '1px solid rgba(99, 102, 241, 0.25)',
                                        }}
                                    />
                                )}

                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: theme.palette.text.disabled }} />

                                {/* Role Chip */}
                                <Chip
                                    label={userContext.role}
                                    size="small"
                                    sx={{
                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        height: 26,
                                        letterSpacing: '-0.01em',
                                    }}
                                />

                                {/* Manager Badge */}
                                {userContext.is_manager && (
                                    <Chip
                                        label="MANAGER"
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                                            color: '#34d399',
                                            fontWeight: 800,
                                            fontSize: '0.7rem',
                                            height: 26,
                                            letterSpacing: '0.05em',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                        }}
                                    />
                                )}

                                {/* Period Status Badge */}
                                {isLocked ? (
                                    <Chip
                                        icon={<Lock sx={{ fontSize: 14, color: '#f87171' }} />}
                                        label="CLOSED"
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(239, 68, 68, 0.15)',
                                            color: '#f87171',
                                            fontWeight: 800,
                                            fontSize: '0.7rem',
                                            height: 26,
                                            letterSpacing: '0.05em',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<LockOpen sx={{ fontSize: 14, color: '#34d399' }} />}
                                        label="OPEN"
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                                            color: '#34d399',
                                            fontWeight: 800,
                                            fontSize: '0.7rem',
                                            height: 26,
                                            letterSpacing: '0.05em',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Admin Actions */}
                {permissions.canCreateOrgOKR && (
                    <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
                        {/* Create Period Button */}
                        <Tooltip
                            title={hasOpenPeriod ? 'Close the current period before creating a new one' : ''}
                            arrow
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    startIcon={<CalendarMonth />}
                                    onClick={dialogs.openCreatePeriodDialog}
                                    disabled={hasOpenPeriod}
                                    sx={{
                                        borderColor: '#10b981',
                                        color: '#10b981',
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        borderRadius: 2.5,
                                        px: 2.5,
                                        textTransform: 'none',
                                        letterSpacing: '-0.01em',
                                        '&:hover': {
                                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                                            borderColor: '#10b981',
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Create Period
                                </Button>
                            </span>
                        </Tooltip>

                        {/* Create Org Goal Button */}
                        <Tooltip
                            title={
                                isViewingNonActive
                                    ? 'Switch to active period to create objectives'
                                    : isLocked
                                    ? 'Period is closed'
                                    : !viewedPeriod?.id
                                    ? 'No active period selected'
                                    : ''
                            }
                            arrow
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={<Add />}
                                    onClick={dialogs.openCreateOrgDialog}
                                    disabled={isLocked || !viewedPeriod?.id || isViewingNonActive}
                                    sx={{
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        borderRadius: 2.5,
                                        px: 2.5,
                                        textTransform: 'none',
                                        letterSpacing: '-0.01em',
                                        '&:hover': {
                                            boxShadow: '0 12px 32px rgba(99, 102, 241, 0.5)',
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Create Org Goal
                                </Button>
                            </span>
                        </Tooltip>

                        {/* New Team Button */}
                        <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<Groups />}
                            onClick={dialogs.openCreateTeamDialog}
                            sx={{
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                borderRadius: 2.5,
                                px: 2.5,
                                textTransform: 'none',
                                letterSpacing: '-0.01em',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            New Team
                        </Button>

                        {/* Close/Reopen Period Button */}
                        <Tooltip
                            title={
                                isLocked
                                    ? 'Reopen this period for editing'
                                    : 'Close this period to prevent changes and enable creating a new period'
                            }
                            arrow
                        >
                            <span>
                                <Button
                                    variant={isLocked ? 'contained' : 'outlined'}
                                    size="medium"
                                    color={isLocked ? 'error' : 'inherit'}
                                    startIcon={isLocked ? <Lock /> : <LockOpen />}
                                    onClick={handleLockToggle}
                                    disabled={!viewedPeriod?.id}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        borderRadius: 2.5,
                                        px: 2.5,
                                        textTransform: 'none',
                                        letterSpacing: '-0.01em',
                                        ...(isLocked && {
                                            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)',
                                            background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                                        }),
                                        '&:hover': { transform: 'translateY(-1px)' },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {isLocked ? 'Reopen Period' : 'Close Period'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DashboardHeader;
